import { useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { api } from '../api'

// The design a print builder opens with, and keeping the changes made to it. A book's maker keeps its design with the
// book (shared/printDesign.ts), so it's the same on any of their devices and it's where everyone else starts. Anyone
// else's changes are just for them, kept on their own device.

export type Keeping = 'saving' | 'saved' | 'failed' | null

export function useDesign<D>({
  book,
  what = 'print',
  storageKey: key,
  kept,
  mine,
  read,
  start,
}: {
  // The book's address in the API and its cached query, as in ['cookbook', id] and /cookbooks/:id.
  book: { kind: 'album' | 'cookbook'; id: string }
  // Which of the book's designs: its printed book's, or its recipe cards'.
  what?: 'print' | 'cards'
  // Where this device keeps the design.
  storageKey: string
  kept: D | null
  mine: boolean
  read: (value: unknown) => D | null
  start: D
}) {
  const queryClient = useQueryClient()
  const here = () => {
    try {
      return read(JSON.parse(localStorage.getItem(key) ?? 'null'))
    } catch {
      return null
    }
  }
  const [design, setDesign] = useState<D>(() => (mine ? (kept ?? here()) : (here() ?? kept)) ?? start)
  const [keeping, setKeeping] = useState<Keeping>(null)
  // Saves go one after another, so the last choice is the one kept.
  const queue = useRef(Promise.resolve())
  const latest = useRef(design)

  const choose = (next: D) => {
    setDesign(next)
    latest.current = next
    if (!mine) {
      try {
        localStorage.setItem(key, JSON.stringify(next))
      } catch {
        // Private windows can refuse; the design just isn't kept.
      }
      return
    }
    setKeeping('saving')
    queue.current = queue.current.then(async () => {
      try {
        await api(`/${book.kind}s/${encodeURIComponent(book.id)}/${what}-design`, { method: 'PUT', body: next })
        queryClient.setQueryData<Record<string, unknown>>([book.kind, book.id], (old) =>
          old ? { ...old, [`${what}Design`]: next } : old,
        )
        if (latest.current === next) setKeeping('saved')
      } catch {
        if (latest.current === next) setKeeping('failed')
      }
    })
  }
  return { design, choose, keeping }
}

// A design's pages with one page's own choices changed. A choice left as the book's is dropped, a page with none left
// goes, and with no pages left the design has none.
export function editPage<E extends Record<string, unknown>>(
  pages: Record<string, E> | undefined,
  key: string,
  edit: E,
): Record<string, E> | undefined {
  const kept = Object.fromEntries(Object.entries(edit).filter(([, v]) => v !== undefined && v !== '')) as E
  const rest = Object.fromEntries(Object.entries(pages ?? {}).filter(([k]) => k !== key))
  const next = Object.keys(kept).length ? { ...rest, [key]: kept } : rest
  return Object.keys(next).length ? next : undefined
}
